import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Users,
  CalendarClock,
  Compass,
  Star,
  Radio,
} from "lucide-react";

const cardStyle = {
  position: "relative",
  background: "#0F0F0F",
  border: "1px solid rgba(192,192,192,0.15)",
  padding: "18px 18px 20px",
  minHeight: 116,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  fontFamily: "Montserrat, sans-serif",
  color: "#F5F1E8",
};

const labelStyle = {
  fontFamily: "Montserrat",
  fontSize: 10,
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  color: "#B89B5E",
  fontWeight: 600,
};

const valueStyle = {
  fontFamily: "Montserrat",
  fontSize: 30,
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.05,
  color: "#F5F1E8",
  wordBreak: "break-word",
};

const subStyle = {
  fontSize: 11,
  letterSpacing: "0.04em",
  color: "#C0C0C0",
  marginTop: 6,
  lineHeight: 1.4,
};

const accentBar = (color) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: 36,
  height: 3,
  background: color,
});

const Card = ({ label, value, sub, icon: Icon, accent, testId }) => (
  <div style={cardStyle} data-testid={testId}>
    <span aria-hidden style={accentBar(accent)} />
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={labelStyle}>{label}</span>
      <Icon size={16} strokeWidth={1.5} color={accent} />
    </div>
    <div>
      <div style={valueStyle}>{value}</div>
      {sub && <div style={subStyle}>{sub}</div>}
    </div>
  </div>
);

const StatsStrip = ({ refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get("/admin/stats");
        if (!cancelled) {
          setData(d);
          setError("");
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || "Stats failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const total = data?.total_leads ?? 0;
  const week = data?.leads_this_week ?? 0;
  const topSrc = data?.top_utm_source;
  const topSvc = data?.top_service;
  const direct = data?.direct_count ?? 0;
  const campaign = data?.campaign_count ?? 0;
  const directPct = total ? Math.round((direct / total) * 100) : 0;
  const campaignPct = total ? Math.round((campaign / total) * 100) : 0;

  return (
    <section
      data-testid="admin-kpi-strip"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
        gap: 12,
        marginBottom: 24,
      }}
      aria-label="Lead source dashboard"
    >
      <Card
        label="Leads · 7d"
        value={week}
        sub="Submitted in the last 7 days"
        icon={CalendarClock}
        accent="#0072CE"
        testId="kpi-week"
      />
      <Card
        label="Total Leads"
        value={total}
        sub="All-time lead capture"
        icon={Users}
        accent="#B89B5E"
        testId="kpi-total"
      />
      <Card
        label="Top UTM Source"
        value={topSrc?.value || "—"}
        sub={topSrc ? `${topSrc.count} lead${topSrc.count === 1 ? "" : "s"} attributed` : "No leads yet"}
        icon={Radio}
        accent="#D6007F"
        testId="kpi-top-source"
      />
      <Card
        label="Top Service"
        value={topSvc?.value || "—"}
        sub={topSvc ? `${topSvc.count} lead${topSvc.count === 1 ? "" : "s"} interested` : "Awaiting service tags"}
        icon={Star}
        accent="#FFD200"
        testId="kpi-top-service"
      />
      <Card
        label="Direct vs Campaign"
        value={`${direct} · ${campaign}`}
        sub={total ? `${directPct}% direct · ${campaignPct}% campaign` : "No leads yet"}
        icon={Compass}
        accent="#C0C0C0"
        testId="kpi-direct-campaign"
      />
      {error && (
        <div
          data-testid="kpi-error"
          style={{
            gridColumn: "1 / -1",
            padding: "10px 14px",
            background: "rgba(214,0,127,0.08)",
            border: "1px solid rgba(214,0,127,0.35)",
            color: "#F5F1E8",
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
    </section>
  );
};

export default StatsStrip;
